import { useCallback, useEffect, useState } from 'react';
import { Gamepad2, Menu, Ticket, Volume2, VolumeX, X } from 'lucide-react';
import Portal from './components/Portal';
import GamePlayer from './components/GamePlayer';
import { games, getGame } from './games/registry';
import { arcadeAudio } from './platform/audio';
import { beginGame, loadSave, recordResult, storeSave, type ArcadeSave } from './platform/persistence';
import type { GameId, GameResult } from './platform/types';

function routeFromHash() {
  const match = window.location.hash.match(/^#\/game\/([^/]+)/);
  return getGame(match?.[1] ?? null);
}

export default function App() {
  const [save, setSave] = useState<ArcadeSave>(() => loadSave());
  const [activeGame, setActiveGame] = useState(routeFromHash);
  const [menuOpen, setMenuOpen] = useState(false);
  const stampCount = Object.values(save.stamps).reduce((count, stamps) => count + (stamps?.length ?? 0), 0);

  useEffect(() => {
    arcadeAudio.setMuted(save.settings.muted);
  }, [save.settings.muted]);

  useEffect(() => {
    const sync = () => setActiveGame(routeFromHash());
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!activeGame) return;
      if (event.key.toLowerCase() === 'f') {
        const cabinet = document.querySelector('.cabinet');
        if (!document.fullscreenElement && document.fullscreenEnabled && cabinet?.requestFullscreen) void cabinet.requestFullscreen().catch(() => undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeGame]);

  const play = useCallback((id: GameId) => {
    setSave((current) => beginGame(current, id));
    window.location.hash = `#/game/${id}`;
    window.scrollTo({ top: 0, behavior: save.settings.reducedMotion ? 'auto' : 'smooth' });
  }, [save.settings.reducedMotion]);

  const exit = useCallback(() => {
    arcadeAudio.stopMusic();
    window.location.hash = '#/';
    setActiveGame(null);
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    setSave((current) => {
      const next = { ...current, settings: { ...current.settings, muted } };
      storeSave(next);
      return next;
    });
  }, []);

  const result = useCallback((game: GameId, nextResult: GameResult) => {
    setSave((current) => recordResult(current, game, nextResult.score, nextResult.stamps));
  }, []);

  const handleActiveResult = useCallback((next: GameResult) => {
    if (activeGame) result(activeGame.id, next);
  }, [activeGame, result]);

  if (activeGame) {
    return <GamePlayer key={activeGame.id} game={activeGame} muted={save.settings.muted} onMutedChange={setMuted} onExit={exit} onResult={handleActiveResult} />;
  }

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">Skip to games</a>
      <header className="site-header">
        <button className="wordmark" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Casper Arcade home">
          <Gamepad2 size={28} /><span>CASPER</span><b>ARCADE</b>
        </button>
        <nav id="main-navigation" className={menuOpen ? 'is-open' : ''} aria-label="Main navigation">
          <a href="#machines" onClick={() => setMenuOpen(false)}>Games <sup>{games.length}</sup></a>
          <a href="#ticket-book" onClick={() => setMenuOpen(false)}>Ticket book</a>
          <a href="#credits" onClick={() => setMenuOpen(false)}>About</a>
        </nav>
        <div className="header-actions">
          <button className="sound-button" onClick={() => setMuted(!save.settings.muted)} aria-label={save.settings.muted ? 'Unmute arcade' : 'Mute arcade'}>{save.settings.muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
          <div className="ticket-counter"><Ticket size={18} /><span>{stampCount}/21</span></div>
          <button className="menu-button" onClick={() => setMenuOpen((open) => !open)} aria-label="Toggle menu" aria-expanded={menuOpen} aria-controls="main-navigation">{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </header>
      <Portal save={save} onPlay={play} />
      <footer className="site-footer" id="credits">
        <div className="site-footer__mark"><Gamepad2 size={34} /><span>CASPER<br />ARCADE</span></div>
        <p>Seven tiny worlds, built with care for curious thumbs and restless keyboards.</p>
        <div className="site-footer__credits"><span>Assets by Kenney, Kay Lousberg / KayKit, and Dumivid · CC0</span><span>Scores and progress stay on this device.</span></div>
        <span className="site-footer__edition">STUDIO EDITION · 2026</span>
      </footer>
    </div>
  );
}
